import axios from 'axios';
import { getBackendUrl } from '../config';

// Get backend URL from runtime config (supports Railway deployment)
const API_BASE_URL = getBackendUrl();

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Users API
export const usersAPI = {
  getUsers: async (params = {}) => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  getUser: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },

  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
};

// Payments API
export const paymentsAPI = {
  getPayments: async (params = {}) => {
    const response = await api.get('/payments', { params });
    return response.data;
  },

  getPayment: async (paymentId) => {
    const response = await api.get(`/payments/${paymentId}`);
    return response.data;
  },

  createPayment: async (paymentData) => {
    const response = await api.post('/payments', paymentData);
    return response.data;
  },

  updatePaymentStatus: async (paymentId, status) => {
    const response = await api.patch(`/payments/${paymentId}/status`, null, {
      params: { status },
    });
    return response.data;
  },

  getPaymentStats: async () => {
    const response = await api.get('/stats/payments');
    return response.data;
  },
};

// Tax Filing API
export const taxFilingAPI = {
  createFiling: async (filingData) => {
    const response = await api.post('/tax-filings', filingData);
    return response.data;
  },

  getFilings: async (params = {}) => {
    const response = await api.get('/tax-filings', { params });
    return response.data;
  },

  getFiling: async (filingId) => {
    const response = await api.get(`/tax-filings/${filingId}`);
    return response.data;
  },

  updateFiling: async (filingId, updateData) => {
    const response = await api.patch(`/tax-filings/${filingId}`, updateData);
    return response.data;
  },

  deleteFiling: async (filingId) => {
    const response = await api.delete(`/tax-filings/${filingId}`);
    return response.data;
  },
};

// LLC Formation API
export const llcAPI = {
  createLLC: async (llcData) => {
    const response = await api.post('/llc-formations', llcData);
    return response.data;
  },

  getLLCs: async (params = {}) => {
    const response = await api.get('/llc-formations', { params });
    return response.data;
  },

  getLLC: async (llcId) => {
    const response = await api.get(`/llc-formations/${llcId}`);
    return response.data;
  },

  updateLLC: async (llcId, updateData) => {
    const response = await api.patch(`/llc-formations/${llcId}`, updateData);
    return response.data;
  },
};

// Documents API
export const documentsAPI = {
  uploadDocument: async (docData) => {
    const response = await api.post('/documents', docData);
    return response.data;
  },

  getDocuments: async (params = {}) => {
    const response = await api.get('/documents', { params });
    return response.data;
  },

  deleteDocument: async (docId) => {
    const response = await api.delete(`/documents/${docId}`);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getAllFilings: async (params = {}) => {
    const response = await api.get('/admin/tax-filings', { params });
    return response.data;
  },

  getAllLLCs: async (params = {}) => {
    const response = await api.get('/admin/llc-formations', { params });
    return response.data;
  },

  getAdminStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
};

// Agent API
export const agentAPI = {
  getAgentStats: async () => {
    const response = await api.get('/agent/stats');
    return response.data;
  },

  getAgentClients: async () => {
    const response = await api.get('/agent/clients');
    return response.data;
  },

  getAgentTasks: async () => {
    const response = await api.get('/agent/tasks');
    return response.data;
  },
};


// Agency API
export const agencyAPI = {
  getAgencyStats: async () => {
    const response = await api.get('/agency/stats');
    return response.data;
  },

  getRecentFilings: async (limit = 5) => {
    const response = await api.get('/agency/recent-filings', { params: { limit } });
    return response.data;
  },
};


// Pricing Plans API
export const pricingAPI = {
  getPlans: async (planType = null, activeOnly = true, isBusinessFiling = null) => {
    const params = {};
    if (planType) params.plan_type = planType;
    if (activeOnly !== undefined) params.active_only = activeOnly;
    if (isBusinessFiling !== null) params.is_business_filing = isBusinessFiling;
    const response = await api.get('/pricing-plans', { params });
    return response.data;
  },

  getPlan: async (planId) => {
    const response = await api.get(`/pricing-plans/${planId}`);
    return response.data;
  },

  createPlan: async (planData) => {
    const response = await api.post('/pricing-plans', planData);
    return response.data;
  },

  updatePlan: async (planId, planData) => {
    const response = await api.patch(`/pricing-plans/${planId}`, planData);
    return response.data;
  },

  deletePlan: async (planId) => {
    const response = await api.delete(`/pricing-plans/${planId}`);
    return response.data;
  },
};


export default api;
